import React from 'react';
import { Activity, Pill, Users, GraduationCap, MapPin, Stethoscope } from 'lucide-react';
import { useLanguage } from '../../src/functions/useLanguage';
import { wt } from '../i18n';

interface FeatureDef {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  titleKey: Parameters<typeof wt>[1];
  descKey: Parameters<typeof wt>[1];
  color: string;
  bg: string;
}

const FEATURES: FeatureDef[] = [
  {
    icon: Activity,
    titleKey: 'features.vitals.title',
    descKey: 'features.vitals.desc',
    color: 'text-primary-brown',
    bg: 'bg-primary-brown/10',
  },
  {
    icon: Pill,
    titleKey: 'features.meds.title',
    descKey: 'features.meds.desc',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
  },
  {
    icon: Users,
    titleKey: 'features.guide.title',
    descKey: 'features.guide.desc',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
  },
  {
    icon: GraduationCap,
    titleKey: 'features.notifications.title',
    descKey: 'features.notifications.desc',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
  },
  {
    icon: MapPin,
    titleKey: 'features.trends.title',
    descKey: 'features.trends.desc',
    color: 'text-cyan-600',
    bg: 'bg-cyan-50',
  },
  {
    icon: Stethoscope,
    titleKey: 'features.rewards.title',
    descKey: 'features.rewards.desc',
    color: 'text-amber-500',
    bg: 'bg-amber-50',
  },
];

export function Features() {
  const { language } = useLanguage();
  const t = (key: Parameters<typeof wt>[1]) => wt(language, key);

  return (
    <section id="features" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2
            className="text-4xl lg:text-5xl text-title-primary mb-2 tracking-tight"
            style={{
              fontFamily: '"Grozen Medical", "Atkinson Hyperlegible", ui-sans-serif, system-ui, sans-serif',
            }}
          >
            <span style={{ fontWeight: 700 }}>The </span>
            <span style={{ fontWeight: 300 }}>Complete </span>
            <span style={{ fontWeight: 700 }}>Toolkit</span>
          </h2>
          <p className="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
            {t('features.subheading')}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map(({ icon: Icon, titleKey, descKey, color, bg }) => (
            <div
              key={titleKey}
              className="group bg-white border border-slate-100 rounded-3xl p-8 transition-all"
            >
              <div
                className={`w-full min-h-12 text-title-primary ${bg} rounded-xl flex items-center gap-3 px-4 py-2 mb-5 transition-transform group-hover:scale-[1.02]`}
              >
                <Icon size={24} className={`${color} shrink-0`} />
                <h3 className="text-base font-display font-normal text-[#0d2744] leading-tight text-left flex-1 whitespace-normal">
                  {t(titleKey)}
                </h3>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{t(descKey)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
